/**
 * 将 /cm/projects/:id 返回的项目对象整理为便于大模型阅读的纯文本（按板块可选）。
 * @file 合约 AI 数据清洗
 */
/* eslint-disable max-len, require-jsdoc, valid-jsdoc */

function line(label, val) {
  if (val === undefined || val === null || val === '') {
    return '';
  }
  return `${label}：${val}`;
}

function money(n) {
  if (n === undefined || n === null || n === '') {
    return '';
  }
  const x = Number(n);
  if (Number.isNaN(x)) {
    return String(n);
  }
  return x.toLocaleString('zh-CN');
}

function billPlanStatus(s) {
  const m = {0: '未开始/进行中', 1: '进行中', 2: '已完成'};
  return m[s] !== undefined ? m[s] : String(s);
}

function attachConditionGroups(p) {
  const out = {...p};
  const groups = Array.isArray(out.condition_groups) ? [...out.condition_groups] : [];
  const conditions = Array.isArray(out.conditions) ? out.conditions : [];
  groups.forEach((g) => {
    g.conditions = conditions.filter((c) => c.business_type === g.business_type);
  });
  out.condition_groups = groups;
  return out;
}

function sectionBasic(p) {
  const lines = [];
  lines.push('【一、项目基础信息】');
  lines.push(line('项目编号', p.code));
  lines.push(line('项目名称', p.name));
  lines.push(line('简称', p.shortname));
  lines.push(line('业务类型', p.type));
  lines.push(line('状态', p.status));
  lines.push(line('合同额(元)', money(p.amount)));
  lines.push(line('签定价(元)', money(p.signed_amount)));
  lines.push(line('调价额(元)', money(p.adjust_amount)));
  lines.push(line('甲方/委托单位', p.partA || p.institution_unit));
  lines.push(line('联系人', p.contact_name));
  lines.push(line('联系电话', p.contact_phone));
  lines.push(line('项目说明', p.description));
  lines.push(line('建设地址', p.buildingAddress || p.address));
  lines.push(line('建筑面积(㎡)', p.buildingArea));
  lines.push(line('建筑高度(m)', p.buildingHeight));
  lines.push(line('单体数量', p.buildingCount));
  lines.push(line('建筑类别', p.buildingCategory));
  lines.push(line('地下室层数', p.basementLevels));
  lines.push(line('建安产值(元)', money(p.building_production)));
  lines.push(line('计划开始', p.started_at));
  lines.push(line('计划竣工', p.completed_at));
  lines.push(line('实际结束', p.finished_at));
  lines.push(line('签约日期', p.signed_at));
  lines.push(line('投标日期', p.bid_at));
  if (Array.isArray(p.groups) && p.groups.length) {
    lines.push(`承担部门：${p.groups.map((g) => g.name).filter(Boolean).join('、')}`);
  }
  if (Array.isArray(p.condition_groups) && p.condition_groups.length) {
    lines.push('— 业务分项与合同条件 —');
    p.condition_groups.forEach((g, i) => {
      lines.push(`  ${i + 1}. ${g.business_type || ''} | 类型:${g.type || '-'} | 金额(元):${money(g.amount)} | 未开票(元):${money(g.unbilling)}`);
      (g.conditions || []).forEach((c, j) => {
        lines.push(`     条款${j + 1}: ${c.detail || '-'} | 比例:${c.percent != null ? `${c.percent}%` : '-'} | 金额(元):${money(c.amount)}`);
      });
    });
  }
  if (Array.isArray(p.contracts) && p.contracts.length) {
    lines.push('— 关联合同摘要 —');
    p.contracts.forEach((c, i) => {
      lines.push(`  ${i + 1}. ${c.name || c.code} | 甲方:${c.partA || '-'} | 业务:${c.business_type || '-'} | 金额(元):${money(c.amount)} | 状态:${c.status || '-'}`);
    });
  }
  if (Array.isArray(p.files) && p.files.length) {
    lines.push(`合同/附件文件：共 ${p.files.length} 个（类型：${[...new Set(p.files.map((f) => f.type).filter(Boolean))].join('、') || '未标注'}）`);
  }
  const imgN = Array.isArray(p.images) ? p.images.length : 0;
  if (imgN) {
    lines.push(`现场图片：共 ${imgN} 张（略 URL）`);
  }
  const dyn = Object.keys(p).filter((k) => /^target_/.test(k));
  if (dyn.length) {
    lines.push('— 年度/目标类扩展字段 —');
    dyn.forEach((k) => lines.push(`  ${k}：${p[k]}`));
  }
  return lines.filter(Boolean).join('\n');
}

function sectionProgress(p) {
  const lines = [];
  lines.push('【二、项目进度（总控计划）】');
  const nodes = Array.isArray(p.nodes) ?
    p.nodes.filter((n) => n.version === 'origin' && n.name && String(n.name).trim()) :
    [];
  nodes.sort((a, b) => (a.order || 0) - (b.order || 0));
  if (!nodes.length) {
    lines.push('（无 origin 版总控节点或节点无名称）');
  } else {
    let sum = 0;
    nodes.forEach((n, i) => {
      sum += Number(n.percent) || 0;
      lines.push(`  ${i + 1}. ${n.name || '未命名节点'} | 权重:${n.percent != null ? `${n.percent}%` : '-'} | 计划开工:${n.plan_started_at || '-'} | 计划工期(天):${n.plan_duration != null ? n.plan_duration : '-'} | 实际开工:${n.started_at || '-'} | 实际完成:${n.finished_at || '-'}`);
      if (Array.isArray(n.hrplans) && n.hrplans.length) {
        lines.push(`     岗位计划：${n.hrplans.map((h) => `${h.position}×系数${h.factor}`).join('；')}`);
      }
    });
    lines.push(line('节点权重合计(%)', sum));
  }
  lines.push(line('当前运作节点ID', p.operate_node_id));
  lines.push(line('计划完成节点ID', p.complete_node_id));
  lines.push(line('总控计划产值进度(当前估算)', p.production_current != null ? money(p.production_current) : ''));
  lines.push(line('总控计划产值(全周期)', p.production_plan != null ? money(p.production_plan) : ''));
  if (Array.isArray(p.adjust_progress_plans) && p.adjust_progress_plans.length) {
    lines.push(`— 进度调整记录：${p.adjust_progress_plans.length} 条（略）`);
  }
  return lines.filter(Boolean).join('\n');
}

function sectionHr(p) {
  const lines = [];
  lines.push('【三、项目人员投入】');
  const hr = Array.isArray(p.hr) ? p.hr : [];
  if (!hr.length) {
    lines.push('（无人员记录）');
    return lines.join('\n');
  }
  hr.forEach((h, i) => {
    const intern = h.intern ? '实习' : '';
    lines.push(`  ${i + 1}. 用户ID:${h.user_id} | 岗位:${h.position} | 系数:${h.factor} | ${intern} | 进场:${h.started_at || '-'} | 离场:${h.finished_at || '-'} | 备注:${h.note || '-'}`);
  });
  if (Array.isArray(p.positions) && p.positions.length) {
    lines.push(`项目岗位字典：${p.positions.join('、')}`);
  }
  return lines.join('\n');
}

function sectionBilling(p) {
  const lines = [];
  lines.push('【四、收费计划 / 开票 / 到账】');
  lines.push(line('合同计划应收(元)', money(p.plan_amount)));
  lines.push(line('已计划完成额(元)', money(p.plan_finished_amount)));
  lines.push(line('已开票(元)', money(p.bill_amount)));
  lines.push(line('未开票(元)', money(p.unbill_amount)));
  lines.push(line('已到账(元)', money(p.trans_amount)));
  lines.push(line('未到账(元)', money(p.untrans_amount)));
  lines.push(line('开票进度', p.bill_percent != null ? `${p.bill_percent}%` : ''));
  lines.push(line('到账占已开票', p.trans_percent != null ? `${p.trans_percent}%` : ''));

  const plans = Array.isArray(p.bill_plans) ? [...p.bill_plans] : [];
  const condMap = {};
  (p.conditions || []).forEach((c) => {
    condMap[c.id] = c;
  });
  if (plans.length) {
    lines.push('— 收费计划 —');
    plans.forEach((bp, i) => {
      const c = condMap[bp.condition_id];
      const title = c ? `${c.business_type} / ${c.detail}` : bp.condition_id;
      lines.push(`  ${i + 1}. ${title} | 金额(元):${money(bp.amount)} | 类型:${bp.type || '-'} | 计划完成:${bp.plan_finished_at || '-'} | 实际完成:${bp.finished_at || '-'} | 状态:${billPlanStatus(bp.status)}`);
    });
  }

  const bills = Array.isArray(p.bills) ? p.bills : [];
  if (bills.length) {
    lines.push('— 开票记录 —');
    bills.forEach((b, i) => {
      lines.push(`  ${i + 1}. 开票日:${b.billed_at || '-'} | 金额(元):${money(b.amount)} | 摘要:${b.detail || b.note || '-'} | 已转:${money(b.transferred_amount)} | 到账比例:${b.transferred_percent != null ? `${b.transferred_percent}%` : '-'} | 最近到账日:${b.transferred_date || '-'}`);
    });
  }

  const transfers = Array.isArray(p.transfers) ? p.transfers : [];
  if (transfers.length) {
    lines.push('— 到账流水 —');
    transfers.forEach((t, i) => {
      lines.push(`  ${i + 1}. 金额(元):${money(t.amount)} | 到账日:${t.transferred_at || '-'} | 备注:${t.note || '-'}`);
    });
  }

  return lines.filter(Boolean).join('\n');
}

function sectionBp(p) {
  const lines = [];
  lines.push('【五、建安工程量】');
  const bp = Array.isArray(p.bp) ? p.bp : [];
  if (!bp.length) {
    lines.push('（无建安工程量台账）');
    return lines.join('\n');
  }
  const sorted = [...bp].sort((a, b) => String(a.recorded_at).localeCompare(String(b.recorded_at)));
  sorted.forEach((row, i) => {
    lines.push(`  ${i + 1}. 统计截止:${row.recorded_at || '-'} | 累计产值(元):${money(row.amount)} | 周期:${row.started_at || '-'} ~ ${row.finished_at || '-'}`);
    if (row.desc) {
      lines.push(`     ${String(row.desc).replace(/\s+/g, ' ').trim()}`);
    }
  });
  return lines.join('\n');
}

function sectionMemoRisk(p) {
  const lines = [];
  lines.push('【六、重要事项及风险提示】');
  const memos = Array.isArray(p.memos) ? [...p.memos] : [];
  memos.sort((a, b) => (a.order || 0) - (b.order || 0));
  if (memos.length) {
    memos.forEach((m, i) => {
      const warn = m.warning ? '【风险/预警】' : '';
      lines.push(`  ${i + 1}. ${warn}${m.detail || '-'} | 记录时间:${m.created_at || '-'}`);
      if (m.note) {
        lines.push(`     备注：${m.note}`);
      }
    });
  } else {
    lines.push('（无重要事项记录）');
  }
  const ev = Array.isArray(p.events) ? p.events : [];
  if (ev.length) {
    lines.push(`— 其他事件：${ev.length} 条（略字段）`);
  }
  return lines.join('\n');
}

const SECTION_BUILDERS = {
  basic: sectionBasic,
  progress: sectionProgress,
  hr: sectionHr,
  billing: sectionBilling,
  bp: sectionBp,
  memo_risk: sectionMemoRisk,
};

/**
 * 按 CM 板块固定顺序排列 key（与勾选顺序无关）。
 * @param {string[]|undefined|null} sections 已选 key；空或未传表示全选
 * @return {string[]}
 */
export function orderSectionKeys(sections) {
  const all = CM_PROJECT_DETAIL_SECTIONS.map((s) => s.key);
  if (!Array.isArray(sections)) {
    return all;
  }
  if (sections.length === 0) {
    return [];
  }
  const set = new Set(sections.filter((k) => SECTION_BUILDERS[k]));
  return all.filter((k) => set.has(k));
}

/**
 * @param {object} project - API data 根对象
 * @param {string[]} sections - 板块 key 列表；空则输出全部板块（固定顺序）
 */
export function buildCmProjectDetailText(project, sections) {
  if (!project || typeof project !== 'object') {
    return '';
  }
  const p = attachConditionGroups(project);
  const keysOrdered = orderSectionKeys(sections);
  const head = [
    '======== 项目管理系统 · 导出文本 ========',
    line('导出对象', `${p.name || ''}（ID:${p.id}）`),
    line('数据更新时间', p.updated_at || p.memo_updated_at || ''),
    '',
  ].filter((x) => x !== '');
  const body = keysOrdered.map((k) => SECTION_BUILDERS[k](p)).filter(Boolean);
  return [...head, ...body].join('\n\n');
}

export const CM_PROJECT_DETAIL_SECTIONS = [
  {key: 'basic', label: '项目基础信息'},
  {key: 'progress', label: '项目进度'},
  {key: 'hr', label: '项目人员数据'},
  {key: 'billing', label: '收费计划/开票/到账'},
  {key: 'bp', label: '建安工程量'},
  {key: 'memo_risk', label: '重要事项及风险提示'},
];
